import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, RefreshCw } from 'lucide-react'

import apiClient from '@/lib/api-client'
import { MpButton, MpPanel } from '@/ui/components'

interface Organization {
  id: string
  name: string
}

/**
 * AuthRedirect Component
 * 
 * Handles root '/' route logic:
 * 1. Fetches user's organizations
 * 2. If organizations exist:
 *    - Redirects to last active org (from localStorage)
 *    - Or redirects to first org in list
 * 3. If no organizations:
 *    - Redirects to onboarding
 */
export function AuthRedirect() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchOrganizationsAndRedirect()
  }, [])

  async function fetchOrganizationsAndRedirect() {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.get<Organization[]>('/api/v1/organizations/')
      const organizations = response.data

      if (organizations && organizations.length > 0) {
        // Check for last active organization
        const lastActiveOrgId = localStorage.getItem('lastActiveOrgId')
        
        // If lastActiveOrgId exists and is in the list, use it
        const targetOrg = lastActiveOrgId 
          ? organizations.find(org => org.id === lastActiveOrgId)
          : null
        
        const orgToRedirect = targetOrg || organizations[0]
        navigate(`/o/${orgToRedirect.id}/dashboard`, { replace: true })
      } else {
        // No organizations - redirect to onboarding
        navigate('/onboarding/create-organization', { replace: true })
      }
    } catch (e: any) {
      console.error('Failed to fetch organizations:', e)
      setError(
        e?.response?.data?.detail || 
        'Не вдалося завантажити організації. Спробуйте ще раз.'
      )
      setLoading(false)
    }
  }

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--mph-bg-app)] px-4">
        <MpPanel className="w-full max-w-md p-6 text-center">
          <p className="text-base font-medium text-[var(--mph-text-primary)]">{error}</p>
          <MpButton onClick={fetchOrganizationsAndRedirect} className="mt-5">
            <RefreshCw className="h-4 w-4" />
            Спробувати ще раз
          </MpButton>
        </MpPanel>
      </div>
    )
  }

  // Loading state
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--mph-bg-app)] px-4">
      <div className="flex items-center gap-3 text-sm text-[var(--mph-text-secondary)]">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--mph-accent)]" />
        Завантаження...
      </div>
    </div>
  )
}
